/**
 * @file CommandPalette.tsx
 * @brief Global Cmd+K / Ctrl+K command palette — rail GridBlock (mega-menu chrome).
 *
 * @details
 * Mounted once in `AdminShellLayout`. Portal overlay under the topbar on the
 * same ~1280px rail as NavMegaMenu / page grids. Combines page jumps, quick
 * actions, typed filters, and live entity search.
 *
 * @dependencies
 * - cmdk (via components/ui/Command)
 * - react-router-dom
 * - features/analytics/components/GridBlock
 *
 * @references
 * - layouts/components/AdminShellLayout.tsx
 * - layouts/components/NavMegaMenu.tsx
 * - .cursor/rules/webpanel-cmdk-palette.mdc
 *
 * @author DES
 * @date 17-07-2026
 * @updated 30-07-2026 — Meeting Intel synonym + richer entity search
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Command as CommandIcon, X } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from "@/components/ui/Command";
import {
  GridBand,
  GridBlock,
} from "@/features/analytics/components/GridBlock";
import { shellOverlayMaxClass } from "@/layouts/components/shell-rail";
import { useAuth } from "@/shared/auth/auth-context";
import { cn } from "@/shared/theme/cn";
import type { NavShellConfig } from "@/layouts/components/nav-types";
import {
  buildActionCommands,
  buildFilterCommands,
  buildPageCommands,
  buildUserActionCommands,
} from "./buildStaticCommands";
import type { CommandPaletteItem, PaletteScope } from "./commandPaletteTypes";
import { HighlightMatch } from "./HighlightMatch";
import { useEntitySearchResults } from "./useEntitySearchResults";

interface CommandPaletteProps {
  nav: NavShellConfig;
  /** Whose palette; defaults by role (super admin, else company). */
  scope?: PaletteScope;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CLOSE_MS = 280;
const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

const GROUP_ORDER: Array<{
  id: string;
  heading: string;
  kinds: CommandPaletteItem["kind"][];
}> = [
  { id: "actions", heading: "Actions", kinds: ["action"] },
  { id: "filters", heading: "Filters", kinds: ["filter"] },
  { id: "pages", heading: "Pages", kinds: ["page"] },
  {
    id: "entities",
    heading: "Results",
    kinds: ["company", "user", "employee", "meeting", "session", "invoice", "note", "todo"],
  },
];

function staggerStyle(visible: boolean, delayMs: number): CSSProperties {
  return {
    transitionProperty: "opacity, transform",
    transitionDuration: "380ms",
    transitionTimingFunction: EASE,
    transitionDelay: visible ? `${delayMs}ms` : "0ms",
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(10px)",
  };
}

export function CommandPalette({
  nav,
  scope: scopeProp,
  open,
  onOpenChange,
}: CommandPaletteProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const scope: PaletteScope = scopeProp ?? (isSuperAdmin ? "superadmin" : "company");
  const [query, setQuery] = useState("");
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }

    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(frame);
    }

    setVisible(false);
    closeTimer.current = setTimeout(() => {
      setMounted(false);
      closeTimer.current = null;
    }, CLOSE_MS);

    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mounted, onOpenChange]);

  useEffect(() => {
    if (!visible) return;
    const id = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => window.clearTimeout(id);
  }, [visible]);

  const pages = useMemo(() => buildPageCommands(nav), [nav]);
  const actions = useMemo(
    () => (scope === "user" ? buildUserActionCommands(nav) : buildActionCommands(isSuperAdmin)),
    [scope, nav, isSuperAdmin],
  );
  const filters = useMemo(
    // Filters jump to admin lists; a normal user has none (21-10-2026).
    () => (scope === "user" ? [] : buildFilterCommands(isSuperAdmin)),
    [scope, isSuperAdmin],
  );
  // What a normal user can search: the modules in their own menu.
  const userModules = useMemo(() => {
    if (scope !== "user") return undefined;
    const paths = pages.map((p) => p.to.split(/[?#]/)[0]);
    return {
      meetings: paths.includes("/app/meetings"),
      notes: paths.includes("/app/notes"),
      todos: paths.includes("/app/todos"),
    };
  }, [scope, pages]);
  const entities = useEntitySearchResults({
    open,
    query,
    scope,
    userModules,
  });

  const allItems = useMemo(
    () => [...actions, ...filters, ...pages, ...entities],
    [actions, filters, pages, entities],
  );

  const runItem = useCallback(
    (item: CommandPaletteItem) => {
      onOpenChange(false);
      navigate(item.to);
    },
    [navigate, onOpenChange],
  );

  const close = () => onOpenChange(false);

  if (!mounted) return null;

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close command palette"
        tabIndex={visible ? 0 : -1}
        className={cn(
          "fixed inset-0 z-40 bg-black/45 backdrop-blur-[3px] transition-opacity duration-300 ease-out",
          visible ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        className={cn(
          "fixed inset-x-0 top-14 z-50 max-h-[calc(100vh-3.5rem)] overflow-y-auto",
          !visible && "pointer-events-none",
        )}
        style={{
          transitionProperty: "opacity, transform",
          transitionDuration: visible ? "420ms" : "240ms",
          transitionTimingFunction: EASE,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(-8px)",
        }}
      >
        <div className={shellOverlayMaxClass}>
          {/* Keeps its own white: menus sit over the page (DES 30-09-2026). */}
          <GridBlock className="bg-background">
            <GridBand
              className="flex items-center justify-between gap-3 py-3"
              style={staggerStyle(visible, 40)}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <CommandIcon className="h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold">Command palette</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {scope === "user"
                      ? "Search your pages, meetings, notes and to-dos."
                      : "Search pages, people, Meeting Intel, sessions, and actions."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-none text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                aria-label="Close command palette"
              >
                <X className="h-4 w-4" />
              </button>
            </GridBand>

            <div style={staggerStyle(visible, 80)}>
              <Command
                className="rounded-none border-0 bg-transparent"
                loop
              >
                <CommandInput
                  ref={inputRef}
                  value={query}
                  onValueChange={setQuery}
                  placeholder={scope === "user" ? "Search your meetings, notes, to-dos…" : "Search pages, people, Meeting Intel…"}
                />
                <CommandList>
                  <CommandEmpty>No matches found.</CommandEmpty>
                  {/* A normal user's own results first while searching: they
                      are what they typed for (21-10-2026). */}
                  {(scope === "user" && query.trim().length >= 2
                    ? [GROUP_ORDER[3], GROUP_ORDER[0], GROUP_ORDER[1], GROUP_ORDER[2]]
                    : GROUP_ORDER
                  ).map((group) => {
                    const items = allItems.filter((item) =>
                      group.kinds.includes(item.kind),
                    );
                    if (items.length === 0) return null;
                    return (
                      <CommandGroup key={group.id} heading={group.heading}>
                        {items.map((item) => {
                          const Icon = item.icon;
                          return (
                            <CommandItem
                              key={item.id}
                              value={`${item.label} ${item.description ?? ""} ${item.keywords ?? ""} ${item.id}`}
                              onSelect={() => runItem(item)}
                            >
                              <Icon className="h-4 w-4 shrink-0 text-foreground" />
                              <span className="min-w-0 flex-1 truncate">
                                <span className="font-medium">
                                  <HighlightMatch
                                    text={item.label}
                                    query={query}
                                  />
                                </span>
                                {item.description ? (
                                  <span className="mt-0.5 block truncate text-xs text-muted-foreground sm:mt-0 sm:ml-2 sm:inline">
                                    <HighlightMatch
                                      text={item.description}
                                      query={query}
                                    />
                                  </span>
                                ) : null}
                              </span>
                              <CommandShortcut className="hidden sm:inline">
                                {item.kind === "todo" ? "to-do" : item.kind}
                              </CommandShortcut>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    );
                  })}
                </CommandList>
              </Command>
            </div>

            <GridBand
              className="flex items-center justify-between gap-3 py-2.5 text-[11px] text-muted-foreground"
              style={staggerStyle(visible, 120)}
            >
              <span>
                <kbd className="font-mono text-[10px]">↑↓</kbd> navigate
                <span className="mx-2 text-border">·</span>
                <kbd className="font-mono text-[10px]">↵</kbd> open
                <span className="mx-2 text-border">·</span>
                <kbd className="font-mono text-[10px]">esc</kbd> close
              </span>
              <span className="hidden sm:inline">
                <kbd className="font-mono text-[10px]">⌘K</kbd> anytime
              </span>
            </GridBand>
          </GridBlock>
        </div>
      </div>
    </>,
    document.body,
  );
}
